/**
 * Voyage Data Composable
 * Handles data fetching for voyage details, summary, and telemetry data
 */

import VoyageMasterSvc from '@/services/VoyageMasterSvc';
import VoyageSummarySvc from '@/services/VoyageSummarySvc';
import AssetSvc from '@/services/AssetSvc';
import LandmarkSvc from '@/services/LandmarkSvc';
import SummarySvc from '@/services/SummarySvc';
import moment from 'moment';

export function useVoyageData() {
  /**
   * Fetch voyage master data by ID
   * @param {string} voyageId - Voyage ID
   * @returns {Promise<Object|null>} Voyage data or null
   */
  const fetchVoyageData = async (voyageId) => {
    try {
      const voyageMasterSvc = new VoyageMasterSvc();
      const response = await voyageMasterSvc.getById(voyageId);

      let voyageData = response.data;
      if (Array.isArray(response.data?.data)) {
        voyageData = response.data.data[0];
      } else if (Array.isArray(response.data)) {
        voyageData = response.data[0];
      } else if (response.data?.data && typeof response.data.data === 'object') {
        voyageData = response.data.data;
      }

      return voyageData;
    } catch (error) {
      console.error('fetchVoyageData error:', error);
      throw error;
    }
  };

  /**
   * Fetch voyage summary by ID
   * @param {string} voyageId - Voyage ID
   * @returns {Promise<Object|null>} Summary data or null
   */
  const fetchSummary = async (voyageId) => {
    try {
      const response = await VoyageSummarySvc.getById(voyageId);
      return response.data?.summary || null;
    } catch (error) {
      console.error('fetchSummary error:', error);
      return null;
    }
  };

  /**
   * Fetch GPS data for a voyage
   * @param {Object} voyage - Voyage object with mvygMassId, mvygStartTime, mvygEndTime
   * @returns {Promise<Object|null>} GPS data or null
   */
  const fetchGpsData = async (voyage) => {
    try {
      if (!voyage || !voyage.mvygMassId) {
        return null;
      }

      const start = moment(voyage.mvygStartTime).unix();
      const end = moment(voyage.mvygEndTime).unix();

      const response = await SummarySvc.getSummary({
        devcType: ['gps'],
        devcMassId: voyage.mvygMassId,
        start,
        end,
        aggregatedUnit: 'HOUR',
        showDetails: true,
      });

      return response.data?.data?.gps || response.data?.gps || response.data || null;
    } catch (error) {
      console.error('fetchGpsData error:', error);
      return null;
    }
  };

  /**
   * Fetch RPM data for a voyage
   * @param {Object} voyage - Voyage object with mvygMassId, mvygStartTime, mvygEndTime
   * @returns {Promise<Object|null>} RPM data or null
   */
  const fetchRpmData = async (voyage) => {
    try {
      if (!voyage || !voyage.mvygMassId) {
        return null;
      }

      const start = moment(voyage.mvygStartTime).unix();
      const end = moment(voyage.mvygEndTime).unix();

      const response = await SummarySvc.getSummary({
        devcType: ['rpm'],
        devcMassId: voyage.mvygMassId,
        start,
        end,
        aggregatedUnit: 'HOUR',
        showDetails: true,
      });

      return response.data?.data?.rpm || response.data?.rpm || response.data || null;
    } catch (error) {
      console.error('fetchRpmData error:', error);
      return null;
    }
  };

  /**
   * Fetch flowmeter (fuel consumption) data for a voyage
   * @param {Object} voyage - Voyage object with mvygMassId, mvygStartTime, mvygEndTime
   * @returns {Promise<Object|null>} Flowmeter data or null
   */
  const fetchFlowmeterData = async (voyage) => {
    try {
      if (!voyage || !voyage.mvygMassId) {
        return null;
      }

      const start = moment(voyage.mvygStartTime).unix();
      const end = moment(voyage.mvygEndTime).unix();

      const response = await SummarySvc.getSummary({
        devcType: ['flowmeter'],
        devcMassId: voyage.mvygMassId,
        start,
        end,
        aggregatedUnit: 'HOUR',
        showDetails: true,
      });

      return response.data?.data?.flowmeter || response.data?.flowmeter || response.data || null;
    } catch (error) {
      console.error('fetchFlowmeterData error:', error);
      return null;
    }
  };

  /**
   * Fetch all assets
   * @returns {Promise<Array>} Array of assets
   */
  const fetchAssets = async () => {
    try {
      const { data } = await AssetSvc.getAssets();
      return data.data || [];
    } catch (error) {
      console.error('fetchAssets error:', error);
      return [];
    }
  };

  /**
   * Fetch landmarks (company and global)
   * @param {number} compId - Company ID
   * @returns {Promise<Array>} Array of landmarks
   */
  const fetchLandmarks = async (compId) => {
    try {
      const [companyLandmarksRes, globalLandmarksRes] = await Promise.all([
        LandmarkSvc.landmarks.get({ params: { compId, max: 1000 } }),
        LandmarkSvc.landmarks.get({ params: { max: 1000 } }),
      ]);

      const allLandmarks = new Map();

      (globalLandmarksRes.data.data || []).forEach((l) => {
        allLandmarks.set(l.lndmId, { ...l, displayName: l.lndmName });
      });

      (companyLandmarksRes.data.data || []).forEach((l) => {
        allLandmarks.set(l.lndmId, { ...l, displayName: l.lndmName });
      });

      return Array.from(allLandmarks.values());
    } catch (error) {
      console.error('fetchLandmarks error:', error);
      return [];
    }
  };

  /**
   * Fetch landmark by ID
   * @param {number} landmarkId - Landmark ID
   * @returns {Promise<Object|null>} Landmark data or null
   */
  const fetchLandmarkById = async (landmarkId) => {
    try {
      const response = await LandmarkSvc.landmarks.getById(landmarkId);
      return response.data?.data?.[0] || null;
    } catch (error) {
      console.error('fetchLandmarkById error:', error);
      return null;
    }
  };

  /**
   * Load all voyage data (convenience method)
   * @param {Object} context - Context object with voyageId and store
   * @returns {Promise<Object>} Object with all loaded data
   */
  const loadAllVoyageData = async ({ voyageId, store }) => {
    const compId = store?.getters?.['auth/activeCompany']?.compId;

    const [voyage, summary, assets, landmarks] = await Promise.all([
      fetchVoyageData(voyageId),
      fetchSummary(voyageId),
      fetchAssets(),
      fetchLandmarks(compId),
    ]);

    return {
      voyage, summary, assets, landmarks,
    };
  };

  /**
   * Load telemetry data (GPS, RPM, Flowmeter) for a voyage
   * @param {Object} voyage - Voyage object
   * @returns {Promise<Object>} Object with gpsData, rpmData, flowmeterData
   */
  const loadTelemetryData = async (voyage) => {
    const [gpsData, rpmData, flowmeterData] = await Promise.all([
      fetchGpsData(voyage),
      fetchRpmData(voyage),
      fetchFlowmeterData(voyage),
    ]);

    return { gpsData, rpmData, flowmeterData };
  };

  return {
    // Individual fetch methods
    fetchVoyageData,
    fetchSummary,
    fetchGpsData,
    fetchRpmData,
    fetchFlowmeterData,
    fetchAssets,
    fetchLandmarks,
    fetchLandmarkById,
    // Convenience methods
    loadAllVoyageData,
    loadTelemetryData,
  };
}

export default useVoyageData;
